import React from 'react'
import { getProfileFormInputs, ProfileFormInputType } from '../inputs'
import { BankAccountTypeEnum, Profile, ProfileForm } from '../types'
import { getIsInputValid } from '../utils'
import { STATES } from '../../../config'
import {
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { DivComponent } from '../styled'
import { ProfileBankAccountTypeRadios } from './radios'

interface Props {
  profileForm: ProfileForm
  blured: Record<string, boolean>
  onChange: (e: React.ChangeEvent<{ value: unknown }>, prop: string) => void
  onBlured: (prop: string) => void
  isAchDebit: boolean
  isProfileCreate?: boolean
  profile?: Profile
}

const getPlaceholder = (value: string, profile: Profile | undefined) => {
  if (!profile) return ''
  if (value === 'cardNumber') return profile.cardNumber
  if (value === 'accountNumber') return profile.accountNumber
  if (value === 'nameOnAccount') return profile.nameOnAccount
  return ''
}

const getIsShrinkActive = (value: string, profile: Profile | undefined, x: ProfileFormInputType) => {
  const isCardNumber = x.value === 'cardNumber'
  const isBankAccountNumber = x.value === 'accountNumber'
  const showCCPlaceholder = isCardNumber && profile?.cardNumber
  const showBankAccountPlaceholder = isBankAccountNumber && profile?.accountNumber
  return !!showCCPlaceholder || !!showBankAccountPlaceholder || !!value
}

export const ProfileFormComponent: React.FC<Props> = ({
  profileForm,
  blured,
  onChange,
  onBlured,
  isAchDebit,
  isProfileCreate,
  profile,
}) => {
  const inputs = React.useMemo(
    () => getProfileFormInputs(isAchDebit),
    [isAchDebit]
  )

  return (
    <Grid container rowSpacing={2} spacing={2}>
      {inputs.map((x) => {
        const isState = x.value === 'state'
        const isEmail = x.value === 'email'
        const isPhoneNumber = x.value === 'phoneNumber'
        const isZip = x.value === 'zip'
        const isCCV = x.value === 'cardCode'
        const isCardNumber = x.value === 'cardNumber'
        const isExpirationDate = x.value === 'expirationDate'
        const isBankAccountType = x.value === 'accountType'

        const isAmex = /^3[47]/.test(profileForm.cardNumber)
        const value = String(profileForm[x.value as keyof ProfileForm])
        const accountNumber = String(
          profileForm['accountNumber' as keyof ProfileForm]
        )
        const isAccountNumberConfirm = x.value === 'accountNumberConfirm'


        const isBlurred = blured[x.value]
        const isError =
          !getIsInputValid({
            isEmail,
            isPhoneNumber,
            isZip,
            isCCV,
            isCardNumber,
            isExpirationDate,
            isAmex,
            isState,
            isAccountNumberConfirm,
            accountNumber,
            value,
          }) && isBlurred

        if (isState) {
          return (
            <Grid key={x.label} item xs={12} sm={6} md={x.md} lg={x.lg}>
              <Select
                value={value}
                required
                error={isError}
                fullWidth
                onChange={(e) =>
                  onChange(e as React.ChangeEvent<{ value: unknown }>, x.value)
                }
                onBlur={() => onBlured(x.value)}
              >
                {STATES.map((x) => (
                  <MenuItem key={x.value} value={x.value}>
                    {x.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )
        }

        if (isBankAccountType) {
          return (
            <Grid key={x.label} item xs={12} sm={6} md={x.md} lg={x.lg}>
              <ProfileBankAccountTypeRadios
                value={value as BankAccountTypeEnum}
                onChange={(e) =>
                  onChange(e as React.ChangeEvent<{ value: unknown }>, x.value)
                }
              />
            </Grid>
          )
        }

        return (
          <Grid key={x.label} item xs={12} sm={6} md={x.md} lg={x.lg}>
            <TextField
              required
              label={x.label}
              fullWidth
              value={value}
              error={isError}
              disabled={isProfileCreate && isEmail}
              onChange={(e) => {
                const _val = e.target.value
                if (isCCV && isAmex && _val?.length > 4) return
                if (isCCV && !isAmex && _val?.length > 3) return
                onChange(e, x.value)
              }}
              onBlur={() => {
                onBlured(x.value)
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{x.icon}</InputAdornment>
                ),
                inputComponent: x.inputComponent as any,
              }}
              inputProps={{
                autoComplete: 'off',
              }}
              InputLabelProps={{
                shrink: getIsShrinkActive(value, profile, x),
              }}
              placeholder={getPlaceholder(x.value, profile)}
            />
          </Grid>
        )
      })}

      <DivComponent>*All Fields Required</DivComponent>
    </Grid>
  )
}
